import { DragEvent, useRef, useState } from 'react';
import { View } from '@shared/components/View';
import { FileDropListenerOverlay } from './overlay';
import { FileDropListenerProps } from './props';
import classNames from './FileDropListener.module.css';

// Built once so the default overlay keeps a stable identity across renders, the
// way it did as a `static defaultProps` value.
const defaultOverlay = <FileDropListenerOverlay />;

/*
    Listens for files dragged over its children: while a file is over them we show an
    overlay, and on drop we hand the files to props.onFilesDropped.

    `dragenter` and `dragleave` fire per element and bubble, so the <form> also sees the
    events its descendants fire — and the new target's `dragenter` arrives before the old
    target's `dragleave`. Dragging in therefore reads as enter, enter, leave, and a plain
    boolean would flick the overlay off while the file is still over us. So we count how
    many nested elements the pointer is inside and only drop the overlay at zero.

    `useCaptureFileDrop` counts for the same reason.
*/
export function FileDropListener({
  overlay = defaultOverlay,
  onFilesDropped,
  disabled,
  ...viewProps
}: FileDropListenerProps) {
  const [isFileHovering, setIsFileHovering] = useState(false);
  // How many nested elements the file is currently over. A ref, not state: it drives
  // nothing on screen and must not re-render mid-drag.
  const dragEnterEventCount = useRef(0);

  const handleFileDragOver = (event: DragEvent<HTMLFormElement>) => event.preventDefault();
  const handleSubmit = (event: DragEvent<HTMLFormElement>) => event.preventDefault();

  const handleFileDragEnter = (event: DragEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!isFileHovering) {
      // a drag that ended off-window or over a child can leave the count
      // unbalanced, so each new drag starts from zero rather than inheriting
      // the previous count
      dragEnterEventCount.current = 0;
      setIsFileHovering(true);
    }

    dragEnterEventCount.current += 1;
  };

  const handleFileDragLeave = (event: DragEvent<HTMLFormElement>) => {
    event.preventDefault();

    dragEnterEventCount.current -= 1;
    if (dragEnterEventCount.current === 0) {
      setIsFileHovering(false);
    }
  };

  const handleFileDrop = (event: DragEvent<HTMLFormElement>) => {
    event.preventDefault();
    // a drop ends the drag with no matching dragleave, so the count never
    // reaches zero on its own
    setIsFileHovering(false);

    const files = event.dataTransfer.files;
    if (files.length) {
      // files is of type FileList, which is a list but doesn't have array
      // functions (eg map). We convert it to an array to make it more easily
      // used
      onFilesDropped(Array.from(files));
    }
  };

  if (disabled) {
    return <View {...viewProps} />;
  }

  return (
    // oxlint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <form
      className={classNames.form}
      onDragEnter={handleFileDragEnter}
      onDragLeave={handleFileDragLeave}
      onDragOver={handleFileDragOver}
      onDrop={handleFileDrop}
      onSubmit={handleSubmit}
    >
      <View {...viewProps} />
      {isFileHovering && <View className={classNames.overlay}>{overlay}</View>}
    </form>
  );
}
